import type { CSSProperties } from 'react';

/**
 * Matches a CSS hex color in `#RGB` or `#RRGGBB` form, case-insensitive.
 */
const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Checks that a value is a hex color the browser can render.
 *
 * Catalog-supplied colors are validated on the backend, but the field is
 * optional and may arrive empty or malformed. Callers use this to decide
 * whether to apply the value or fall back to a design-system color.
 */
export const isValidHexColor = (value?: string | null): value is string =>
  typeof value === 'string' && HEX_COLOR_PATTERN.test(value.trim());

/**
 * Builds the inline style that applies catalog-supplied badge colors.
 *
 * Returns `undefined` when neither color is usable, so the badge keeps its
 * design-system styling untouched. The background is mirrored onto the border
 * so a filled badge keeps the same box size as variant-styled badges, which
 * always draw a 1px border.
 */
export const buildBadgeColorStyle = (
  backgroundColor?: string | null,
  textColor?: string | null,
): CSSProperties | undefined => {
  const style: CSSProperties = {};

  if (isValidHexColor(backgroundColor)) {
    style.background = backgroundColor;
    style.borderColor = backgroundColor;
  }

  if (isValidHexColor(textColor)) {
    style.color = textColor;
  }

  return Object.keys(style).length ? style : undefined;
};
